import { useCallback, useEffect, useRef, useState } from 'react'
import {
  api, ApiError, can, prettyPhone,
  type Conversation, type ConversationFilter, type ConversationThread,
  type ThreadMedia, type ThreadMessage,
} from '../lib/api'
import { readValue, writeValue } from '../lib/prefs'
import { SaveNumber } from './SaveNumber'
import { NewConversation } from './NewConversation'
import { ClipButton, PictureRow, usePictures } from './PictureTray'
import './messaging.css'

/** THE MESSAGING TAB (items 20-24, 23 Sep 2026).
 *
 *  One place for every text that came in and every reply that went out:
 *  conversations on the left, one per person, the conversation on the right.
 *  The strip above Open calls is gone from the dispatch screen unless a role
 *  ticks it back (item 20); this is where texts live now.
 *
 *  A TEXT IS STILL NOT AN ACCEPTANCE. PLAN.html §02 step 5, unchanged. A
 *  text linked to an open call says so in words and offers to open the
 *  call, where the dispatcher logs who is going exactly as he does for a
 *  voice on the radio. There is no Accept button here and there never
 *  should be.
 *
 *  THE NUMBER SHOWS FOR AN UNKNOWN SENDER ONLY (the customer, Q2 A): the way
 *  the Caller card shows a caller's number. A member's or a contact's stays
 *  behind the name. */

const FILTERS: { key: ConversationFilter; label: string }[] = [
  { key: 'unread', label: 'Unread' },
  { key: 'all', label: 'All' },
  { key: 'members', label: 'Members' },
  { key: 'phonebook', label: 'Phone book' },
  { key: 'unknown', label: 'Unknown numbers' },
  { key: 'on_call', label: 'On a call' },
]
/** THE ROW OF FOUR (6 Oct 2026, his Q1 C): All · Unread · Members · More,
 *  the rest under More -- like the four views over Open calls. */
const MAIN_FILTERS: ConversationFilter[] = ['all', 'unread', 'members']
const MORE_FILTERS: ConversationFilter[] = ['phonebook', 'unknown', 'on_call']
const FILTER_PREF = 'messaging.filter'
const POLL_MS = 15_000

function whenLabel(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
  if (sameDay) return time
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) return `yesterday ${time}`
  return `${d.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${time}`
}

function kb(size: number): string {
  return size >= 1_000_000 ? `${(size / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(size / 1000))} KB`
}

function personName(c: { kind: string; name: string | null; number: string | null }): string {
  if (c.kind === 'unknown') return c.number ? prettyPhone(c.number) : 'Unknown number'
  return c.name ?? (c.kind === 'member' ? 'A member' : 'Phone book')
}

function preview(c: Conversation): string {
  const body = (c.last_body ?? '').split('\n')[0].trim()
  const media = c.last_media > 0
    ? (c.last_media === 1 ? 'Attachment' : `${c.last_media} attachments`)
    : ''
  const text = body || media || '(no text)'
  return c.last_direction === 'out' ? `You: ${text}` : text
}

function Attachment({ a }: { a: ThreadMedia }) {
  if (!a.ready) {
    return a.error
      ? <span className="broken">Attachment could not be fetched: {a.error}</span>
      : <span className="pending">Fetching the attachment…</span>
  }
  const src = api.mediaUrl(a.id)
  const mime = a.mime ?? ''
  const size = a.size ? `, ${kb(a.size)}` : ''
  if (mime.startsWith('image/')) {
    return (
      <>
        <img src={src} alt="A picture that came with the text"
             onClick={() => window.open(src, '_blank', 'noopener')} />
        <span className="file"><a href={src} download>Download the picture</a> ({mime}{size})</span>
      </>
    )
  }
  if (mime.startsWith('audio/')) {
    // A phone's voice note is AMR or 3GPP more often than not, which not
    // every browser plays. The download is there for that case.
    return (
      <>
        <audio controls preload="none" src={src} />
        <span className="file"><a href={src} download>Download the voice note</a> ({mime}{size})</span>
      </>
    )
  }
  if (mime.startsWith('video/')) {
    return (
      <>
        <video controls preload="metadata" src={src} />
        <span className="file"><a href={src} download>Download</a> ({mime}{size})</span>
      </>
    )
  }
  return <span className="file"><a href={src} download>Attachment</a> ({mime || 'unknown type'}{size})</span>
}

function Bubble({ m, onOpenCall }: {
  m: ThreadMessage
  onOpenCall?: (id: string, open: boolean) => void
}) {
  if (m.direction === 'in') {
    const media = m.media ?? []
    const pending = m.media_pending ?? 0
    return (
      <div className="msg in">
        {m.body}
        {(media.length > 0 || pending > 0) && (
          <div className="att">
            {media.map((a) => <Attachment a={a} key={a.id} />)}
            {pending > 0 && (
              <span className="pending">
                {pending === 1 ? 'One attachment' : `${pending} attachments`} came with this text
                before attachments were kept; the text server's link has expired.
              </span>
            )}
          </div>
        )}
        <span className="meta">
          {whenLabel(m.at)}
          {m.incident_number && ` · ${m.incident_number}`}
        </span>
        {m.incident_id && m.incident_number && m.incident_open && (
          <div className="callnote">
            A text is not an acceptance. If he is going, log it on {m.incident_number} the
            way you would for a voice on the radio.{' '}
            {onOpenCall && (
              <button type="button" onClick={() => onOpenCall(m.incident_id!, true)}>
                Open {m.incident_number} →
              </button>
            )}
          </div>
        )}
      </div>
    )
  }
  // A GROUP MESSAGE HE WAS SENT (066): the question his reply answers. Not
  // a reply of ours -- it went to the whole group -- so it is drawn apart.
  if (m.direction === 'group') {
    const how = (m.channels ?? []).map((c) => c === 'sms' ? 'text' : c).join(' + ')
    return (
      <div className="msg group">
        {m.subject && <b className="subj">{m.subject}</b>}
        {/* ITS PICTURES (070), from our own copy behind the session. */}
        {(m.sent_media ?? []).length > 0 && (
          <div className="att">
            {(m.sent_media ?? []).map((p) => (
              <a key={p.id} href={`/api/messaging/sent-pictures/${p.id}`} target="_blank" rel="noreferrer">
                <img src={`/api/messaging/sent-pictures/${p.id}`} alt="Picture sent" />
              </a>
            ))}
          </div>
        )}
        {m.body}
        <span className="meta">
          Group message to {(m.audience ?? []).join(', ') || 'a group'} · {whenLabel(m.at)}
          {how && ` · ${how}`}
        </span>
      </div>
    )
  }
  const word = m.status === 'delivered' ? 'delivered ✓'
    : m.status === 'sent' ? 'sent'
    : m.status === 'failed' ? `NOT SENT — ${m.error ?? 'the text server refused it'}`
    : 'sending…'
  return (
    <div className={`msg out ${m.status === 'failed' ? 'failed' : ''}`}>
      {(m.sent_media ?? []).length > 0 && (
        <div className="att">
          {(m.sent_media ?? []).map((p) => (
            <a key={p.id} href={`/api/messaging/sent-pictures/${p.id}`} target="_blank" rel="noreferrer">
              <img src={`/api/messaging/sent-pictures/${p.id}`} alt="Picture sent" />
            </a>
          ))}
        </div>
      )}
      {m.body}
      <span className="meta">
        {whenLabel(m.at)}{m.sent_by_label && ` · ${m.sent_by_label}`} · {word}
      </span>
    </div>
  )
}

export function Messaging({ me, unread = 0, onOpenCall, onRead }: {
  /** Who is signed in: a text we start is signed with this name. */
  me: { display_name: string }
  /** Unread texts across every conversation: the tab badge's own number,
   *  so the Unread button and the badge can never disagree. */
  unread?: number
  /** Open a call from a text about it: `open` says whether it is still on
   *  the board (the dispatch screen) or closed (Past calls). */
  onOpenCall?: (id: string, open: boolean) => void
  /** The count on the tab should be looked at again. */
  onRead?: () => void
}) {
  const [filter, setFilter] = useState<ConversationFilter>(
    () => readValue(FILTER_PREF, FILTERS.map((f) => f.key), 'unread') as ConversationFilter)
  const [list, setList] = useState<Conversation[]>([])
  const [listError, setListError] = useState<string | null>(null)
  const [key, setKey] = useState<string | null>(null)
  /** The search box (his Q2 A): a name, a unit, or an unknown sender's
   *  number -- only what the list already shows, so nothing is found by a
   *  number the screen keeps behind a name. */
  const [search, setSearch] = useState('')
  const [moreOpen, setMoreOpen] = useState(false)
  /** "+ New conversation" is open on the right (6 Oct 2026). */
  const [starting, setStarting] = useState(false)
  const [thread, setThread] = useState<ConversationThread | null>(null)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  /** Pictures waiting to go with the reply (068). */
  const pics = usePictures()
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const bottom = useRef<HTMLDivElement>(null)
  /** Held in a ref so a new arrow from the shell -- which re-renders on
   *  radio traffic and its own polls -- does not rebuild `loadThread` and
   *  restart the effect below, which would close a half-filled Save-to-
   *  phone-book form and wipe the "Sent." note. */
  const onReadRef = useRef(onRead)
  useEffect(() => { onReadRef.current = onRead }, [onRead])
  /** WHOSE CONVERSATION IS OPEN, readable by a response that arrives late.
   *  A read of A landing after B was picked used to put A's texts on screen
   *  while the reply box still sent to B -- a text to the wrong phone. A
   *  response for anybody but the open conversation is dropped, and a reply
   *  goes to the conversation ON SCREEN, by its own key. */
  const keyRef = useRef<string | null>(null)

  const loadList = useCallback(async () => {
    try {
      setList(await api.conversations(filter))
      setListError(null)
    } catch (e) {
      setListError(e instanceof ApiError ? e.message : 'Could not load the conversations.')
    }
  }, [filter])
  useEffect(() => {
    loadList()
    const t = setInterval(loadList, POLL_MS)
    return () => clearInterval(t)
  }, [loadList])

  /** OPENING A CONVERSATION READS IT. Every unread text from this person is
   *  marked read the moment it is on screen, and again as new ones arrive
   *  while it stays open -- read, not handled, and certainly not accepted. */
  const loadThread = useCallback(async (k: string) => {
    try {
      const th = await api.conversation(k)
      if (keyRef.current !== k) return
      setThread(th)
      if (th.messages.some((m) => m.direction === 'in' && !m.read_at)) {
        await api.conversationRead(k).catch(() => {})
        setList((l) => l.map((c) => (c.key === k ? { ...c, unread: 0 } : c)))
        onReadRef.current?.()
      }
    } catch (e) {
      if (keyRef.current !== k) return
      if (e instanceof ApiError && e.status === 404) { setKey(null); setThread(null) }
    }
  }, [])
  // A new conversation starts clean; only a new one. The words typed for the
  // last person go too: left in the box, they are one press from somebody
  // they were not written for.
  useEffect(() => { setNote(null); setSaving(false); setText(''); pics.clear() }, [key])  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    keyRef.current = key
    // The last person's texts leave the screen at once, not when the next
    // person's arrive.
    setThread((t) => (t && t.key === key ? t : null))
    if (!key) return
    loadThread(key)
    const t = setInterval(() => loadThread(key), POLL_MS)
    return () => clearInterval(t)
  }, [key, loadThread])
  const count = thread?.messages.length ?? 0
  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }) }, [count, key])

  const moreBtn = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  // Opened, the menu takes the focus: the chosen item, else the first.
  useEffect(() => {
    if (!moreOpen) return
    const items = [...(menu.current?.querySelectorAll('button') ?? [])]
    ;(items.find((b) => b.getAttribute('aria-checked') === 'true') ?? items[0])?.focus()
  }, [moreOpen])
  // The More menu closes on a click anywhere else, or Escape.
  useEffect(() => {
    if (!moreOpen) return
    const away = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.cv-bar')) setMoreOpen(false)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMoreOpen(false) }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc) }
  }, [moreOpen])

  function choose(f: ConversationFilter) {
    setMoreOpen(false)
    setFilter(f)
    writeValue(FILTER_PREF, f)
  }

  async function send() {
    // TO THE CONVERSATION ON SCREEN, by its own key -- never a remembered one.
    if (!thread || sending || pics.busy || (!text.trim() && pics.ids.length === 0)) return
    const to = thread.key
    setSending(true)
    setNote(null)
    try {
      const out = await api.conversationReply(to, text.trim(), pics.ids)
      if (keyRef.current === to) {
        setText('')
        pics.clear()
        setNote(out.status === 'failed'
          ? { ok: false, text: `Not sent: ${out.error ?? 'the text server refused it'}. It is kept here as not sent.` }
          : { ok: true, text: 'Sent.' })
      }
      await loadThread(to)
      loadList()
    } catch (e) {
      setNote({ ok: false, text: e instanceof ApiError ? e.message : 'Could not send the reply.' })
    } finally {
      setSending(false)
    }
  }

  const selected = list.find((c) => c.key === key) ?? null
  const label = (f: ConversationFilter) => FILTERS.find((x) => x.key === f)?.label ?? f
  /** On the More button a chosen filter must fit a quarter of 340 px. */
  const short = (f: ConversationFilter) => (f === 'unknown' ? 'Unknown' : label(f))
  const needle = search.trim().toLowerCase()
  const digits = needle.replace(/\D/g, '')
  const shown = !needle ? list : list.filter((c) =>
    [c.name, c.callsign].some((v) => v && v.toLowerCase().includes(needle))
    || (digits.length >= 3 && c.kind === 'unknown' && (c.number ?? '').replace(/\D/g, '').includes(digits)))
  const inMore = MORE_FILTERS.includes(filter)
  const who = thread ? personName(thread) : ''
  const openCall = thread
    ? [...thread.messages].reverse().find((m) => m.direction === 'in' && m.incident_id && m.incident_open)
    : undefined

  return (
    <div className={`msgtab ${key || starting ? 'showing' : ''}`}>
      <div className="col list">
        <h2>
          Conversations <span className="count">{shown.length}</span>
          <span className="spacer" />
          {/* THE WHOLE TAB ONLY (075): one started with anybody but a live
              call's caller would leave a narrow tab the moment it was sent. */}
          {can('messaging.reply') && can('messaging.view_all') && (
            <button type="button" className="btn sm nc-go" onClick={() => { setKey(null); setStarting(true) }}>
              + New conversation
            </button>
          )}
        </h2>
        <div className="cv-bar">
          <div className="cv-seg" role="tablist" aria-label="Which conversations">
            {MAIN_FILTERS.map((f) => (
              <button type="button" role="tab" key={f} aria-selected={filter === f}
                      className={filter === f ? 'cv-on' : ''} onClick={() => choose(f)}>
                {label(f)}
                {f === 'unread' && unread > 0 && <span className="cv-n">{unread}</span>}
              </button>
            ))}
            <button type="button" role="tab" aria-selected={inMore} ref={moreBtn}
                    aria-haspopup="menu" aria-expanded={moreOpen}
                    className={inMore ? 'cv-on' : ''} onClick={() => setMoreOpen((o) => !o)}
                    onKeyDown={(e) => { if (e.key === 'ArrowDown') { e.preventDefault(); setMoreOpen(true) } }}>
              {inMore ? short(filter) : 'More'} ▾
            </button>
          </div>
          {moreOpen && (
            <div className="cv-menu" role="menu" ref={menu}
                 onKeyDown={(e) => {
                   const items = [...(menu.current?.querySelectorAll('button') ?? [])]
                   const at = items.indexOf(document.activeElement as HTMLButtonElement)
                   if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                     e.preventDefault()
                     const next = (at + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length
                     items[next]?.focus()
                   } else if (e.key === 'Escape') {
                     e.preventDefault(); setMoreOpen(false); moreBtn.current?.focus()
                   } else if (e.key === 'Tab') {
                     setMoreOpen(false)
                   }
                 }}>
              {MORE_FILTERS.map((f) => (
                <button type="button" role="menuitemradio" key={f} aria-checked={filter === f}
                        className={filter === f ? 'cv-on' : ''}
                        onClick={() => { choose(f); moreBtn.current?.focus() }}>
                  {label(f)}
                </button>
              ))}
            </div>
          )}
          <input className="cv-search" type="search" value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search a name, unit or number" aria-label="Search conversations" />
        </div>
        <div className="convs">
          {listError && <div className="msgnote warn">{listError}</div>}
          {!listError && list.length > 0 && shown.length === 0 && (
            <div className="msgempty">
              <div className="big">Nobody here matches “{search.trim()}”</div>
              <button type="button" className="btn sm" onClick={() => setSearch('')}>Clear the search</button>
            </div>
          )}
          {!listError && list.length === 0 && (
            <div className="msgempty">
              <div className="big">
                {filter === 'unread' ? 'Nothing unread'
                  : can('messaging.view_all') ? 'No conversations' : 'No texts for open calls'}
              </div>
              {!can('messaging.view_all') && (
                <p>You see the texts of calls that are open, or closed a moment ago and still
                  inside the reopen window.</p>
              )}
              {filter !== 'all' && (
                <button type="button" className="btn sm" onClick={() => choose('all')}>Show all</button>
              )}
            </div>
          )}
          {shown.map((c) => (
            <button type="button" key={c.key}
                    className={`conv ${c.key === key ? 'on' : ''} ${c.unread > 0 ? 'unread' : ''}`}
                    onClick={() => { setStarting(false); setKey(c.key) }}>
              <span className="who">
                <span className="name">{personName(c)}</span>
                {c.callsign && <small>{c.callsign}</small>}
                {c.kind === 'contact' && <small>phone book</small>}
              </span>
              <span className="when">{whenLabel(c.last_at)}</span>
              <span className="last">
                {c.unread > 0 && <span className="n">{c.unread}</span>}
                <span className="txt">{preview(c)}</span>
                {c.incident_number && !c.live_call && <small className="mono">· {c.incident_number}</small>}
              </span>
              {c.live_call && (
                <span className={`lc-chip ${c.live_call.open ? 'lc-open' : 'lc-closed'}`}>
                  {c.live_call.number} · {c.live_call.open ? 'open'
                    : `closed${c.live_call.clears_at
                      ? `, clears at ${new Date(c.live_call.clears_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                      : ''}`}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="col thread">
        {starting ? (
          <NewConversation me={me} onCancel={() => setStarting(false)}
                           onStarted={(k) => { setStarting(false); loadList(); setKey(k) }} />
        ) : !thread ? (
          <div className="msgempty">
            <div className="big">Pick a conversation</div>
            <p>Texts that came in, and what was said back, one person at a time.</p>
          </div>
        ) : (
          <>
            <div className="msghead">
              <button type="button" className="btn sm back" onClick={() => setKey(null)}>← Back</button>
              <b>{who}</b>
              {thread.callsign && <span className="pill mono">{thread.callsign}</span>}
              {thread.kind === 'contact' && <span className="pill">phone book</span>}
              {thread.kind === 'unknown' && <span className="pill">unknown number</span>}
              {thread.on_duty === true && <span className="pill go">on duty</span>}
              {thread.on_duty === false && <span className="pill">off duty</span>}
              <span className="spacer" />
              {openCall && onOpenCall && (
                <button type="button" className="btn sm"
                        onClick={() => onOpenCall(openCall.incident_id!, true)}>
                  Open {openCall.incident_number}
                </button>
              )}
              {!openCall && selected?.incident_id && onOpenCall && (
                <button type="button" className="btn sm"
                        onClick={() => onOpenCall(selected.incident_id!, true)}>
                  Open {selected.incident_number}
                </button>
              )}
              {thread.kind === 'unknown' && thread.number && can('phonebook.add') && !saving && (
                <button type="button" className="btn sm" onClick={() => setSaving(true)}>
                  Save to phone book
                </button>
              )}
            </div>

            {saving && thread.number && (
              <div className="bd" style={{ borderBottom: '1px solid var(--line)' }}>
                <SaveNumber
                  phone={thread.number}
                  onCancel={() => setSaving(false)}
                  onDone={async (contact) => {
                    setSaving(false)
                    try {
                      // HIS TEXTS FOLLOW HIM INTO THE BOOK, so the conversation
                      // does not split into before and after.
                      const r = await api.conversationAttach(thread.key, contact.id)
                      setKey(r.key)
                      loadList()
                    } catch (e) {
                      setNote({ ok: false, text: e instanceof ApiError ? e.message : 'Saved, but his texts could not be moved.' })
                    }
                  }}
                />
              </div>
            )}

            <div className="msgthread">
              {thread.messages.map((m) => <Bubble m={m} key={m.id} onOpenCall={onOpenCall} />)}
              <div ref={bottom} />
            </div>

            {can('messaging.reply') ? (
              thread.can_reply ? (
                <>
                  <PictureRow pictures={pics.pictures} onRemove={pics.remove} />
                  <div className="msgcompose">
                    <ClipButton onFiles={pics.add} disabled={sending} />
                    <textarea rows={2} value={text} disabled={sending}
                              placeholder={`Reply to ${who}…`}
                              onChange={(e) => setText(e.target.value)}
                              onKeyDown={(e) => {
                                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); send() }
                              }} />
                    <button type="button" className="btn primary"
                            disabled={sending || pics.busy || (!text.trim() && pics.ids.length === 0)} onClick={send}>
                      {sending ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                  <div className="msgsigned">
                    Goes out from the organization's texting number, signed with your name. No header, no footer.
                  </div>
                </>
              ) : (
                <div className="msgnote warn">
                  {thread.sms_on
                    ? 'There is no number to reply to.'
                    : 'Texting is not switched on for this organization (Settings → Connections → SMS).'}
                </div>
              )
            ) : (
              <div className="msgnote">Your role can read texts but not reply.</div>
            )}
            {note && <div className={`msgnote ${note.ok ? '' : 'warn'}`}>{note.text}</div>}
          </>
        )}
      </div>
    </div>
  )
}
